/**
 * The SPA side of the i18n plumbing (web plan W3.0): the locale route element
 * gives its page the i18next instance of its LITERAL `/{cc}/{lang}` segment
 * (`/pk/en` → `en`); un-prefixed routes get DEFAULT_LOCALE's (`en`). Never the
 * browser language, a cookie, storage or `?lng=` (master D-11).
 */
import React, { useContext, useEffect, useMemo } from 'react';
import { I18nContext, I18nextProvider, setI18n } from 'react-i18next';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { DEFAULT_LOCALE, htmlAttributes, type Locale } from '@/seo/locales';
import { formattingLocale, i18nFor, translatorOf, type Translate } from './index';

// Components rendered outside a locale route (the shell before a route
// matches, components tested on their own) read DEFAULT_LOCALE's language,
// never a detected one.
setI18n(i18nFor(DEFAULT_LOCALE));

/**
 * Provides `locale.lang`'s instance to `useTranslation()` / `useTranslate()`
 * below it, and keeps `<html lang>` on the locale's formatting locale
 * (`en-PK`), as the SEO server writes it (server/head.mjs).
 */
export function LocaleLanguage({
  locale,
  children,
}: {
  locale: Locale;
  children?: React.ReactNode;
}): React.ReactElement {
  const instance = i18nFor(locale);
  const lang = formattingLocale(locale);
  // `dir="rtl"` for a right-to-left language only (web plan MW10): a
  // left-to-right page never carries the attribute.
  const dir = htmlAttributes(locale).dir;
  useEffect(() => {
    if (document.documentElement.lang !== lang) document.documentElement.lang = lang;
    if (dir) {
      if (document.documentElement.dir !== dir) document.documentElement.dir = dir;
    } else if (document.documentElement.hasAttribute('dir')) {
      document.documentElement.removeAttribute('dir');
    }
  }, [lang, dir]);
  return (
    <I18nextProvider i18n={instance} defaultNS="common">
      {children}
    </I18nextProvider>
  );
}

/** The language of the current URL's literal prefix (DEFAULT_LOCALE's without one), for the app shell. */
export function RouteLanguage({ children }: { children?: React.ReactNode }): React.ReactElement {
  return <LocaleLanguage locale={useRouteLocale()}>{children}</LocaleLanguage>;
}

/**
 * The page's `Translate` (its locale route's instance): what the pages pass to
 * `@/seo/pageHeads` / `@/seo/templates`, so the SPA's titles come from the same
 * catalogue the SEO server uses. Outside a locale route, the URL prefix's
 * language (else DEFAULT_LOCALE's).
 */
export function useTranslate(): Translate {
  const context = useContext(I18nContext);
  const locale = useRouteLocale();
  const instance = context?.i18n ?? i18nFor(locale);
  return useMemo(() => translatorOf(instance), [instance]);
}
